import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import styles from "./authForm.module.css";
import { AuthStepper } from "./AuthStepper";

const baseURL = process.env.REACT_APP_BACKEND_API_URL;

const Signup: React.FC = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    givenName: "",
    familyName: "",
    gender: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    role: "patient",
    device: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState<"idle" | "signup">("idle");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading("signup");
    try {
      await axios.post(`${baseURL}api/auth/signup`, {
        ...formData,
        address: `${formData.street} ${formData.city} ${formData.state} ${formData.zip}`.trim(),
      });

      // Navigate to the verify step, carrying the email across.
      navigate("/confirm", {
        state: { email: formData.email },
      });
    } catch (err: any) {
      const data = err.response?.data;
      const msg =
        data?.detail ?? data?.message ?? err.message ?? "Signup failed.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setLoading("idle");
    }
  };

  const disabledSignup =
    !formData.email ||
    !formData.password ||
    !formData.givenName ||
    !formData.familyName ||
    !formData.street ||
    !formData.city ||
    !formData.state ||
    !formData.zip ||
    loading !== "idle";

  return (
    <div className={styles.pane}>
      <AuthStepper current={1} />

      <header className={styles.head}>
        <h2 className={styles.title}>Create your account</h2>
        <p className={styles.lead}>
          One account covers sessions, orders, and your dashboard.
        </p>
      </header>

      <form onSubmit={handleSignup} className={styles.form} noValidate>
        {error && (
          <p className="ui-banner ui-banner--error" role="alert">
            {error}
          </p>
        )}

        <div className="ui-field">
          <label className="ui-label" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            name="email"
            className="ui-input"
            type="email"
            autoComplete="email"
            inputMode="email"
            required
            placeholder="you@example.com"
            value={formData.email}
            onChange={handleChange}
          />
        </div>

        <div className="ui-field">
          <label className="ui-label" htmlFor="password">
            Password
          </label>
          <div className={styles.secretField}>
            <input
              id="password"
              name="password"
              className={`ui-input ${styles.secretInput}`}
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              placeholder="Create a password"
              value={formData.password}
              onChange={handleChange}
            />
            <button
              type="button"
              className={styles.reveal}
              aria-label={showPassword ? "Hide password" : "Show password"}
              onClick={() => setShowPassword((v) => !v)}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
          <p className="ui-help">Use at least 8 characters.</p>
        </div>

        <div className="ui-field-grid ui-field-grid--pair">
          <div className="ui-field">
            <label className="ui-label" htmlFor="givenName">
              First name
            </label>
            <input
              id="givenName"
              name="givenName"
              className="ui-input"
              autoComplete="given-name"
              required
              value={formData.givenName}
              onChange={handleChange}
            />
          </div>
          <div className="ui-field">
            <label className="ui-label" htmlFor="familyName">
              Last name
            </label>
            <input
              id="familyName"
              name="familyName"
              className="ui-input"
              autoComplete="family-name"
              required
              value={formData.familyName}
              onChange={handleChange}
            />
          </div>
        </div>

        <p className="ui-help">
          Health care providers: sign up here, then ask the mRehab team to turn on
          provider access for your account.
        </p>

        <div className="ui-field">
          <label className="ui-label" htmlFor="gender">
            Gender <span className="ui-label-optional">(optional)</span>
          </label>
          <input
            id="gender"
            name="gender"
            className="ui-input"
            placeholder="However you describe it"
            value={formData.gender}
            onChange={handleChange}
          />
        </div>

        <div className="ui-field">
          <label className="ui-label" htmlFor="street">
            Street address
          </label>
          <input
            id="street"
            name="street"
            className="ui-input"
            autoComplete="address-line1"
            required
            placeholder="123 Main St"
            value={formData.street}
            onChange={handleChange}
          />
          <p className="ui-help">Where printed accessories would ship.</p>
        </div>

        <div className="ui-field">
          <label className="ui-label" htmlFor="city">
            City
          </label>
          <input
            id="city"
            name="city"
            className="ui-input"
            autoComplete="address-level2"
            required
            value={formData.city}
            onChange={handleChange}
          />
        </div>

        <div className="ui-field-grid ui-field-grid--pair">
          <div className="ui-field">
            <label className="ui-label" htmlFor="state">
              State
            </label>
            <input
              id="state"
              name="state"
              className="ui-input"
              autoComplete="address-level1"
              required
              placeholder="NY"
              value={formData.state}
              onChange={handleChange}
            />
          </div>
          <div className="ui-field">
            <label className="ui-label" htmlFor="zip">
              Zip code
            </label>
            <input
              id="zip"
              name="zip"
              className="ui-input"
              autoComplete="postal-code"
              inputMode="numeric"
              pattern="[0-9]*"
              required
              placeholder="10001"
              value={formData.zip}
              onChange={handleChange}
            />
          </div>
        </div>

        <button
          type="submit"
          className={`ui-btn ui-btn--lg ui-btn--full ${styles.submit}`}
          disabled={disabledSignup}
          aria-busy={loading === "signup"}
        >
          {loading === "signup" ? "Creating account..." : "Create account"}
        </button>
      </form>

      <div className={styles.links}>
        <p>
          Already have an account?{" "}
          <Link to="/login" className="ui-link">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Signup;
